import { httpsCallable } from 'firebase/functions';
import { functions } from '@lib/firebase';
import environment from '@lib/environment';

let cachedToken: string | null = null;
let tokenExpireTime: number | null = null;

interface CSRFTokenResponse {
  token: string;
}

export const fetchCSRFToken = async (force: boolean = false): Promise<string> => {
  const now = Date.now();
  if (!force && cachedToken && tokenExpireTime && now < tokenExpireTime) {
    return cachedToken;
  }

  try {
    const getCSRFTokenFn = httpsCallable<unknown, CSRFTokenResponse>(functions, 'getCSRFToken');
    const result = await getCSRFTokenFn({});
    cachedToken = result.data.token;
    tokenExpireTime = now + (14 * 60 * 1000);
    return cachedToken;
  } catch (error: any) {
    console.error('Failed to fetch CSRF token:', error);
    // Log more details about the error
    if (error.code) console.error('Error code:', error.code);
    if (error.message) console.error('Error message:', error.message);
    if (error.details) console.error('Error details:', error.details);
    throw new Error(`Failed to fetch CSRF token: ${error.message}`);
  }
};

export const getCSRFToken = async (force: boolean = false): Promise<string> => {
  return fetchCSRFToken(force);
};

export const attachCSRFToken = async (headers: Record<string, string> = {}): Promise<Record<string, string>> => {
  const token = await getCSRFToken();
  const tokenName = environment.security.csrfTokenName;

  return {
    ...headers,
    [tokenName]: token
  };
};

export const getCSRFTokenFromForm = async (formElement: HTMLFormElement | null): Promise<string> => {
  const input = formElement?.querySelector(`input[name="${environment.security.csrfTokenName}"]`) as HTMLInputElement | null;
  return input?.value || (await getCSRFToken());
};

export const clearCSRFToken = (): void => {
  cachedToken = null;
  tokenExpireTime = null;
};

/**
 * Validates a CSRF token (client-side placeholder/simple check)
 * Actual validation MUST happen on the server.
 * @param {string} token - The token to validate
 * @param {string} expectedToken - The expected token value
 * @returns {boolean} - Whether the tokens match
 */
export const validateCSRFToken = (token: string | null, expectedToken: string | null): boolean => {
  if (!token || !expectedToken) return false;
  return token === expectedToken;
};
